// ===========================================
// Meet & Greet Settings Component
// ===========================================
// Manage which teachers can host meet & greet sessions

import { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  Avatar,
  Chip,
  Alert,
  Skeleton,
  Divider,
} from '@mui/material';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { teachersApi, Teacher } from '../../api/users.api';
import { userKeys } from '../../hooks/useUsers';
import { useInvalidateTeacherRosterCaches } from '../../hooks/useSlotCacheInvalidation';
import { usePermissions } from '../../hooks/usePermissions';

export default function MeetAndGreetSettings() {
  const invalidateRosterCaches = useInvalidateTeacherRosterCaches();
  const { enqueueSnackbar } = useSnackbar();
  // PATCH /teachers/:id/meet-and-greet is gated on people_teachers.write.
  const { can } = usePermissions();
  const canToggleHosts = can('people_teachers', 'write');
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  // Fetch all teachers
  const { data: teachers, isLoading } = useQuery({
    // Shares cache with useTeachers(). A bare ['teachers'] key is a DISJOINT
    // key space from ['users','teachers'], so it was unreachable by every
    // existing userKeys.teachers() invalidation.
    queryKey: userKeys.teachers(),
    queryFn: () => teachersApi.getAll(),
  });

  // Mutation to toggle meet & greet hosting
  const toggleMutation = useMutation({
    mutationFn: ({ teacherId, canHost }: { teacherId: string; canHost: boolean }) =>
      teachersApi.toggleMeetAndGreet(teacherId, canHost),
    onMutate: ({ teacherId }) => {
      setUpdatingId(teacherId);
    },
    onSuccess: (_, { canHost }) => {
      // Roster change: canHostMeetAndGreet feeds the PUBLIC M&G availability
      // payload (getPublicAvailability filters on it) and the eligibility
      // pickers, none of which live under userKeys. The wrapper covers the
      // teacher list too, so it replaces the old ['teachers'] invalidation.
      invalidateRosterCaches();
      enqueueSnackbar(
        canHost ? 'Teacher can now host meet & greet sessions' : 'Teacher removed from meet & greet hosting',
        { variant: 'success' }
      );
    },
    onError: () => {
      enqueueSnackbar('Failed to update teacher settings', { variant: 'error' });
    },
    onSettled: () => {
      setUpdatingId(null);
    },
  });

  const handleToggle = (teacher: Teacher) => {
    toggleMutation.mutate({
      teacherId: teacher.id,
      canHost: !teacher.canHostMeetAndGreet,
    });
  };

  // Filter active teachers only
  const activeTeachers = teachers?.filter((t: Teacher) => t.isActive) || [];
  const hostsCount = activeTeachers.filter((t: Teacher) => t.canHostMeetAndGreet).length;

  if (isLoading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Skeleton variant="text" width={200} height={32} />
        <Skeleton variant="text" width="100%" height={24} sx={{ mt: 1 }} />
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rectangular" height={72} sx={{ mt: 2 }} />
        ))}
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Meet & Greet Hosts
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Select which teachers can host meet & greet sessions with prospective families.
        Only enabled teachers will appear as available on the public booking page.
      </Typography>

      {hostsCount === 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          No teachers are currently enabled to host meet & greet sessions.
          Enable at least one teacher below to allow public bookings.
        </Alert>
      )}

      {hostsCount > 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {hostsCount} teacher{hostsCount !== 1 ? 's' : ''} enabled for meet & greet hosting
        </Alert>
      )}

      <List>
        {activeTeachers.map((teacher: Teacher, index: number) => (
          <Box key={teacher.id}>
            {index > 0 && <Divider />}
            <ListItem sx={{ py: 2 }}>
              <Avatar
                sx={{
                  mr: 2,
                  bgcolor: teacher.color || 'primary.main',
                  width: 48,
                  height: 48,
                }}
              >
                {teacher.User.firstName[0]}
                {teacher.User.lastName[0]}
              </Avatar>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                      {teacher.User.firstName} {teacher.User.lastName}
                    </Typography>
                    {teacher.canHostMeetAndGreet && (
                      <Chip label="Host" size="small" color="success" />
                    )}
                  </Box>
                }
                secondary={
                  <Box sx={{ mt: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {teacher.User.email}
                    </Typography>
                    {teacher.TeacherInstrument && teacher.TeacherInstrument.length > 0 && (
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                        {teacher.TeacherInstrument.map((ti) => (
                          ti.Instrument && (
                            <Chip
                              key={ti.id}
                              label={ti.Instrument.name}
                              size="small"
                              variant={ti.isPrimary ? 'filled' : 'outlined'}
                              sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                          )
                        ))}
                      </Box>
                    )}
                  </Box>
                }
              />
              <ListItemSecondaryAction>
                <Switch
                  checked={teacher.canHostMeetAndGreet}
                  onChange={() => handleToggle(teacher)}
                  disabled={!canToggleHosts || updatingId === teacher.id}
                  color="success"
                />
              </ListItemSecondaryAction>
            </ListItem>
          </Box>
        ))}
      </List>

      {activeTeachers.length === 0 && (
        <Alert severity="info">
          No active teachers found. Please add teachers first before configuring meet & greet settings.
        </Alert>
      )}
    </Paper>
  );
}
